import _ from 'lodash'
import qs from 'query-string'
import * as Type from 'redux/actions/types'
import { getEntryPoint, getFilterObject } from './helpers'
import {
  ADDITIONAL_SEARCHBAR_SELECTS,
  BOOKABLE_TYPES,
  entryPointId,
  mappedEntryPointRoutes,
} from './constants'

/**
 * getter for get params
 * @param resultType
 * @param prevParams
 * @returns {{}}
 */
export const getSearchGetParams = ({ resultType, prevParams }) => {
  let getParams = {}

  switch (resultType) {
    case entryPointId.HOSPITALS:
      // TODO params for hospital search not named same? (FIX)
      getParams = {
        key: prevParams.key,
        type: prevParams.type,
        query: prevParams.query,
      }
      break

    case entryPointId.DOCTORS:
      getParams = {
        key: prevParams.key,
        type: prevParams.type,
      }
      break

    case entryPointId.CAREHOMES:
    case entryPointId.CARESERVICES:
    case entryPointId.DAYANDNIGHTCARE:
      Object.keys(ADDITIONAL_SEARCHBAR_SELECTS[resultType]).forEach((param) => {
        const getParam = ADDITIONAL_SEARCHBAR_SELECTS[resultType][param].typ
        getParams[getParam] = prevParams[getParam]
      })
      break
  }

  return getParams
}

/**
 *
 * @param store
 * @param overwriteParams
 * @returns {[string, string]}
 */
export const buildUrl = (store, overwriteParams) => {
  const entryPoint = getEntryPoint()
  const { search, filter } = store
  const { activeSort: storeSorting, collection: storeCollection } = filter
  const { params: storeSearchParams } = search

  // merge store collection with new collection
  // and get Object with selected filter options
  let collection = {}
  let mergedCollection = {}

  // avoid adding filter set to entities which did not have these filters
  if (entryPoint === entryPointId.CROSS) {
    collection = overwriteParams.collection
    mergedCollection = getFilterObject(collection)
  } else {
    collection = _.merge(storeCollection, overwriteParams.collection)
    mergedCollection = getFilterObject(Object.values(collection))
  }

  // remove collection property so its not appended to url as collection param
  delete overwriteParams.collection

  const storeParams = {
    sorting: storeSorting,
    ...storeSearchParams,
  }

  // merge store params with provided overwriteParams
  const mergedParams = _.mergeWith(
    storeParams,
    overwriteParams,
    (valA, valB, key) => {
      // radius can be 0 which means federal wide
      // but "!0" in js is true
      if (key === 'radius') {
        if (valB === 0) return valB
        if (!valB) return valA
      }
      if (!valB) return valA
    }
  )

  const apiUrl = `${ entryPoint }/${ getSearchUrl({
    ...mergedParams,
    ...mergedCollection,
    initial_search: mergedParams?.initial_search || !search.clientHasSearched,
    // initial_search: !search.clientHasSearched,
    // initial_search: initialSearch,  // MGMNAVSUP-548 && MGMNAVSUP-549
    // TODO: check how this edge case can reproduce and fixed
    sorting:
      mergedParams.sorting === 'distance' && !mergedParams.location
        ? 'alphabetical'
        : mergedParams.sorting,
  }) }`

  // construct get url
  const getUrl = getSearchUrl({
    ...mergedParams,
    size: mergedParams.from + mergedParams.size,
    sorting: mergedParams.sorting,
    ...mergedCollection,
    from: null, // from is not required in get url
  })

  return [apiUrl, getUrl]
}

/**
 * url settings for bookmarks:
 * 'ids' comma separated list of bookmark ids
 * 'type' [doctors, hospitals]
 * 'searchKey', 'searchType'
 */
export const getLocationParams = (locationSearch) => {
  const parsed = qs.parse(locationSearch)
  const ids = parsed.ids?.split(',') || []
  const { resultType } = parsed

  return {
    ...parsed,
    ids,
    valid: ids.length > 0 && BOOKABLE_TYPES.includes(resultType),
  }
}

/**
 * url get params
 * @param {Object} params
 * @param {Boolean} encodeValues get uri encoded values
 * @returns {string}
 * NOTICE: array value will not encoded
 */
export const getSearchUrl = (params, encodeValues = true) =>
  Object.keys(params).reduce((result, key) => {
    // handle array (filters)
    if (Array.isArray(params[key])) {
      if (params[key].length > 0) {
        // append all filter key from array
        params[key].forEach((filterKey) => {
          result = `${ result }&${ filterKey }=true`
        })
        return result
      }
      return result
    }

    if (params[key]) {
      const value = encodeValues ? encodeURIComponent(params[key]) : params[key]
      if (!result) return `${ result }?${ key }=${ value }`
      return `${ result }&${ key }=${ value }`
    }

    return result
  }, '')

/**
 * get detail url for a result
 * NOTICE: filter all falsy values ( "", 0, false, null, undefined )
 * @param params
 */
export const getDetailURL = (params) => {
  const { id, entryPoint } = params

  const mappedRoute = mappedEntryPointRoutes[entryPoint]
  const filteredParams = getFilteredParams(params)

  let baseDetailUrl = `${ mappedRoute }/${ id }/`

  if (Object.keys(filteredParams).length > 0) {
    const detailUrlParams = getSearchUrl(filteredParams, true)

    baseDetailUrl = `${ baseDetailUrl }${ detailUrlParams }`
  }

  // return baseDetailUrl + location.search
  return baseDetailUrl
}

/**
 * build the share url with all get params
 * @param details
 * @param type
 * @param searchParams
 * @param currentSort
 * @returns {string}
 */
export const getBookmarkShareUrl = (
  details,
  type,
  searchParams,
  currentSort = false
) => {
  // generate url with selected details params
  if (!details) return ''

  // prepare base prams
  const detailUrls = details.results.map((item) => item.id)
  const url = window.location.href.replace(/\/$/, '')

  const params = {
    resultType: type,
    ids: detailUrls.join(','),
    ...getFilteredParams(searchParams),
    ...(currentSort && { currentSort }),
  }

  return qs.stringifyUrl({
    url: `${ url }/`,
    query: { ...params },
  })
}

/**
 * build the compare url with all get params
 * @param details
 * @param type
 * @param source
 * @param searchParams
 * @returns {string}
 */
export const getComparePath = (details, type, source, searchParams) => {
  if (!details) return ''

  const detailUrls = details.results.map((item) => item.id)
  return qs.stringifyUrl({
    url: '/vergleich/',
    query:
      source === Type.SOURCE_TYPE_LOCAL
        ? {
            // type,
            resultType: type,
            source,
          }
        : {
            // type,
            resultType: type,
            ids: detailUrls.join(','),
            ...searchParams,
            source,
          },
  })
}

/**
 * get koro deeplink
 * @param params
 * @returns {string}
 */
export const getKoroDeepLink = (params) => {
  const { redirectURL, koroParams } = params
  const newSearchParams = {
    ...koroParams,
    key: '8-837',
    query:
      'Behandlung am Herzen oder den Herzkranzgefäßen durch einen Schlauch im Blutgefäß (Katheter)',
  }

  const redirectKoroUrl = `${ redirectURL }${ getSearchUrl(newSearchParams) }`
  return `<a href='${ redirectKoroUrl }' rel='noopener noreferrer' data-cy='koro-redirect-link' >Hier</a>`
}

/**
 * plain compare link (currently used on searchbar and tab change [compare container])
 * notice url without any ids
 * @param resultType
 * @param source
 * @returns {string}
 */
export const getPlainComparePath = (resultType, source) =>
  // qs.stringifyUrl({ url: '/vergleich/', query: { source, type } })
  qs.stringifyUrl({ url: '/vergleich/', query: { source, resultType } })

/**
 * 1.) filter not allowed keys
 * 2.) filter all falsy values ( "", 0, false, null, undefined )
 *
 * @param params
 * @returns {*}
 */
const getFilteredParams = (params) => {
  const notAllowed = ['id', 'entryPoint', 'searchKey', 'searchType']

  // MODIFIED IF LAT/LNG SHOULD SKIPPED FOR SOME TYPES
  // const appendLocation = GOOGLE_DISTANCE_CALC.includes(entryPoint)
  // lat: GOOGLE_DISTANCE_CALC.includes(entryPoint) ? lat : null,
  // lng: GOOGLE_DISTANCE_CALC.includes(entryPoint) ? lng : null,

  // filter not allowed keys
  const filteredParams = Object.keys(params)
    .filter((key) => !notAllowed.includes(key))
    .reduce((obj, key) => {
      obj[key] = params[key]
      return obj
    }, {})

  if (Object.keys(filteredParams).length > 0) {
    return Object.entries(filteredParams).reduce(
      (a, [k, v]) => (v ? ((a[k] = v), a) : a),
      {}
    )
  }

  return {}
}

/**
 * returns pageType for detail view based on url
 * @param entryPointPath
 * @returns pageType
 */
export const getPageTypeFromUrl = (entryPointPath) => {
  // detail page type needed if user opened detail page e.g. from cross search
  switch (`/${ entryPointPath }`) {
    case mappedEntryPointRoutes.carehomes:
      return entryPointId.CAREHOMES
    case mappedEntryPointRoutes.careservices:
      return entryPointId.CARESERVICES
    case mappedEntryPointRoutes.dayandnightcare:
      return entryPointId.DAYANDNIGHTCARE
    case mappedEntryPointRoutes.caresupport:
      return entryPointId.CARESUPPORT
    case mappedEntryPointRoutes.doctors:
      return entryPointId.DOCTORS
    case mappedEntryPointRoutes.hospitals:
    default:
      return entryPointId.HOSPITALS
  }
}
